import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { defaultCrumbsForPath } from "@/components/app-header/routeTitles";
import { parseCasePath } from "@/features/caseIde/routes";

import { readHistorySource } from "./historySource";

export type HistoryNavOptions = {
  /**
   * Where Back goes when there is no in-app history (deep link, new tab):
   * the page's logical parent, e.g. `/cases` for a case. Omit for pages with
   * no parent; Back is then disabled at the start of the session.
   */
  fallbackBackTo?: string | null;
};

export type HistoryNavState = {
  canGoBack: boolean;
  canGoForward: boolean;
  goBack: () => void;
  goForward: () => void;
  /** Tooltip text, e.g. "Back to Cases"; plain "Back" when unknown. */
  backLabel: string;
  forwardLabel: string;
};

/** Human label for a destination path, from the route titles. */
function destinationLabel(verb: string, path: string | null): string {
  if (!path) return verb;
  const pathname = path.split("?")[0];
  const crumbs = defaultCrumbsForPath(pathname);
  const last = crumbs[crumbs.length - 1]?.label;
  if (typeof last === "string") return `${verb} to ${last}`;
  if (parseCasePath(pathname)) return `${verb} to case`;
  return verb;
}

/**
 * Back / Forward that mirror the browser's own history (so the header
 * chevrons and the browser buttons always agree), plus an "up to the parent"
 * fallback when the session has no history to go back to.
 */
export function useHistoryNav({
  fallbackBackTo
}: HistoryNavOptions = {}): HistoryNavState {
  // Subscribes to location changes so the source is re-read after every
  // traversal; the values themselves live in the browser / router.
  useLocation();
  const navigate = useNavigate();
  const source = readHistorySource();

  const fallback = fallbackBackTo?.trim() || null;
  const { canGoBack: hasHistory, canGoForward } = source;
  const canGoBack = hasHistory || Boolean(fallback);

  const goBack = useCallback(() => {
    if (hasHistory) {
      navigate(-1);
      return;
    }
    if (fallback) navigate(fallback);
  }, [fallback, hasHistory, navigate]);

  const goForward = useCallback(() => {
    if (canGoForward) navigate(1);
  }, [canGoForward, navigate]);

  return {
    canGoBack,
    canGoForward,
    goBack,
    goForward,
    backLabel: destinationLabel(
      "Back",
      hasHistory ? source.prevPath : fallback
    ),
    forwardLabel: destinationLabel("Forward", source.nextPath)
  };
}
